/**
 * Separation Policy — HR policy document (resignation + termination),
 * re-exports version helpers from confirmation policy, plus print HTML.
 */

export {
  clonePolicySnapshot,
  computeVersionOnSave,
  getPolicySnapshotsForDisplay,
  mergePolicySnapshots,
} from "./confirmationPolicyDocument";

import { buildApplicabilityProse } from "./confirmationPolicyDocument";

import {
  MASTERS_DOCUMENT_COMPANY_ADDRESS,
  MASTERS_DOCUMENT_COMPANY_NAME,
} from "./masters/companyDocumentDefaults";

function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function masterName(list, id) {
  if (id == null || id === "") return null;
  const f = (list || []).find((x) => String(x.id) === String(id));
  return f?.name || null;
}

function yn(v) {
  if (v === true || v === "yes") return true;
  if (v === false || v === "no") return false;
  return null;
}

/** Hierarchical section numbers: 1, 2, 2.1, 5.1, 5.1.1, … (same as confirmation policy document). */
function createDocNumberer() {
  return {
    m: 0,
    s: 0,
    ss: 0,
    sss: 0,
    major() {
      this.m++;
      this.s = 0;
      this.ss = 0;
      this.sss = 0;
      return String(this.m);
    },
    sub() {
      this.s++;
      this.ss = 0;
      this.sss = 0;
      return `${this.m}.${this.s}`;
    },
    subsub() {
      this.ss++;
      this.sss = 0;
      return `${this.m}.${this.s}.${this.ss}`;
    },
    subsubsub() {
      this.sss++;
      return `${this.m}.${this.s}.${this.ss}.${this.sss}`;
    },
  };
}

function separationTriggerLabel(ev) {
  const map = {
    resignation_approval: "Upon resignation approval",
    resignation_cancellation: "Upon resignation cancellation",
    termination_approval: "Upon termination approval",
    termination_cancellation: "Upon termination cancellation",
  };
  return map[ev] || ev || "—";
}

const CHECKLIST_DEPT_LABELS = {
  "": "—",
  hr: "Human Resources",
  it: "Information Technology",
  admin: "Administration",
  finance: "Finance",
};

const ACTION_ROLE_LABELS = {
  manager: "Manager",
  hr: "HR",
  management: "Management",
  reporting_manager: "Reporting Manager",
  it: "IT",
  admin: "Administration",
  finance: "Finance",
};

const APPROVAL_METHOD_LABELS = {
  auto_approved: "Automatic approval (when eligibility rules are met)",
  workflow: "Multi-step workflow (sequential approvals with SLAs)",
  on_instance: "On-instance / case-by-case approval",
};

const LETTER_RESIGN_LABELS = {
  "": "—",
  standard: "Standard relieving letter",
  acceptance: "Resignation acceptance letter",
  custom_1: "Custom template 1",
};

const LETTER_TERM_LABELS = {
  "": "—",
  standard: "Standard termination letter",
  exit: "Exit / Full & Final letter",
  custom_1: "Custom template 1",
};

function approvalMethodLabel(m) {
  return APPROVAL_METHOD_LABELS[m] || m || "—";
}

function formatDueRuleLong(item) {
  const days = item.dueDateDays != null ? Number(item.dueDateDays) : 7;
  if (item.dueDateLogic === "approval_date") {
    return `Due within <strong>${escapeHtml(String(days))}</strong> calendar day(s) from the <strong>date the triggering approval is recorded</strong> in the system (typically measured from approval timestamp for that event).`;
  }
  if (item.dueDateLogic === "fixed_days") {
    return `Due within <strong>${escapeHtml(String(days))}</strong> calendar day(s) from the <strong>occurrence of the trigger event</strong> (e.g. approval or cancellation as selected).`;
  }
  return escapeHtml(item.dueDateLogic || "—");
}

function formatDueRuleShort(item) {
  const days = item.dueDateDays != null ? Number(item.dueDateDays) : 7;
  if (item.dueDateLogic === "approval_date") return `${days} calendar day(s) from approval date`;
  if (item.dueDateLogic === "fixed_days") return `${days} calendar day(s) from trigger`;
  return item.dueDateLogic || "—";
}

/**
 * @param {object} policy
 * @param {object} options — companyName, companyAddress, preparedBy, approvedBy, workflowRoleLabel,
 *   branchesList, departmentsList, … (same as confirmation for applicability)
 */
export function buildSeparationPolicyDocumentHtml(policy, options = {}) {
  const p = policy || {};
  const org = options.companyName || MASTERS_DOCUMENT_COMPANY_NAME;
  const companyName = escapeHtml(org);
  const companyAddressRaw = options.companyAddress ?? MASTERS_DOCUMENT_COMPANY_ADDRESS;
  const preparedBy = options.preparedBy || "Human Resources";
  const approvedBy = options.approvedBy || "Management";
  const roleLabel =
    options.workflowRoleLabel ||
    ((role) => {
      const map = {
        manager: "Manager",
        hr: "HR",
        management: "Management",
        reporting_manager: "Reporting Manager",
      };
      return map[role] || role || "—";
    });

  const ver = p.policyVersion != null ? p.policyVersion : 1;
  const eff = escapeHtml(p.effectiveDate || "—");
  const policyTitle = escapeHtml(p.policyName || p.name || "Separation policy");

  const resWf = Array.isArray(p.workflowSteps) ? p.workflowSteps : [];
  const termWf = Array.isArray(p.terminationWorkflowSteps) ? p.terminationWorkflowSteps : [];
  const cancelWf = Array.isArray(p.cancellationWorkflowSteps) ? p.cancellationWorkflowSteps : [];

  const noticeTypeProb =
    p.noticeProbationType === "working" ? "Working days" : "Calendar days";
  const noticeTypeConf =
    p.noticeConfirmedType === "working" ? "Working days" : "Calendar days";
  const probDays = p.defaultNoticeProbationDays ?? p.defaultNoticePeriodDays ?? 30;
  const confDays = p.defaultNoticeConfirmedDays ?? p.defaultNoticePeriodDays ?? 30;

  const noticeCodesShared = (() => {
    if (Array.isArray(p.leaveCodesForNotice) && p.leaveCodesForNotice.length) {
      return p.leaveCodesForNotice.join(", ");
    }
    const prob =
      Array.isArray(p.leaveCodesProbation) && p.leaveCodesProbation.length
        ? p.leaveCodesProbation
        : Array.isArray(p.attendanceStatusesProbation) && p.attendanceStatusesProbation.length
          ? p.attendanceStatusesProbation
          : [];
    if (prob.length) return prob.join(", ");
    const conf =
      Array.isArray(p.leaveCodesConfirmed) && p.leaveCodesConfirmed.length
        ? p.leaveCodesConfirmed
        : Array.isArray(p.attendanceStatusesConfirmed) && p.attendanceStatusesConfirmed.length
          ? p.attendanceStatusesConfirmed
          : [];
    return conf.length ? conf.join(", ") : "—";
  })();

  const checklist = Array.isArray(p.checklistMappings) ? p.checklistMappings : [];

  const approvalMethodProse =
    p.approvalMethod === "workflow"
      ? "Resignation, termination, and cancellation decisions under this policy follow the multi-step approval workflows configured below. Each step must be completed within the service levels described, unless escalated or varied by HR for a particular case."
      : p.approvalMethod === "on_instance"
        ? "Approvals may be initiated and decided on a case-by-case basis. Where workflow steps are attached to an individual case, the same step order and timelines apply to that case."
        : "Certain events may be recorded or closed when eligibility rules are satisfied, without a full manual chain. HR retains oversight and may intervene where required.";

  const pushedNote =
    p.policyDocumentPushed && p.policyPushedAt
      ? `<p class="meta"><strong>Employee publication:</strong> This version was published to covered employees on ${escapeHtml(
          new Date(p.policyPushedAt).toLocaleString()
        )}.</p>`
      : "";

  const addressBlock = companyAddressRaw
    ? `<p class="addr">${escapeHtml(companyAddressRaw).replace(/\n/g, "<br/>")}</p>`
    : "";

  const noticeTableBody = `<tr>
    <td>Employees in probation</td>
    <td>${escapeHtml(noticeTypeProb)}</td>
    <td><strong>${escapeHtml(String(probDays))}</strong></td>
    <td>${
      p.noticeProbationType === "working"
        ? `Each day marked with one of: <strong>${escapeHtml(noticeCodesShared)}</strong> counts as one working day toward notice.`
        : "—"
    }</td>
  </tr>
  <tr>
    <td>Confirmed employees</td>
    <td>${escapeHtml(noticeTypeConf)}</td>
    <td><strong>${escapeHtml(String(confDays))}</strong></td>
    <td>${
      p.noticeConfirmedType === "working"
        ? `Same attendance / status codes as probation: <strong>${escapeHtml(noticeCodesShared)}</strong>.`
        : "—"
    }</td>
  </tr>`;

  const docNum = createDocNumberer();

  const wfStepsHtml = (steps, titleWithoutNumber) => {
    const sec = docNum.sub();
    const fullTitle = `${sec} ${titleWithoutNumber}`;
    if (!steps.length) {
      return `<h3>${escapeHtml(fullTitle)}</h3><p>For this path, no separate approval steps are defined in the current version. HR will apply the general approval approach above and route the case in line with company practice and applicable law.</p>`;
    }
    const scheduleRows = steps
      .map((s, i) => {
        const r = roleLabel(s.role);
        const notify =
          s.notifyOnly === true
            ? "Yes — information only (no approval action)"
            : "No — approval action required";
        let waiver = "Not permitted";
        if (s.noticeWaiverAllowed) {
          waiver =
            s.noticeWaiverMaxDays != null && Number(s.noticeWaiverMaxDays) > 0
              ? `Permitted — up to ${escapeHtml(String(s.noticeWaiverMaxDays))} day(s)`
              : "Permitted — maximum days to be set by HR / system";
        }
        const sla = s.slaDays ?? 3;
        return `<tr>
          <td>${i + 1}</td>
          <td>${escapeHtml(r)}</td>
          <td><strong>${escapeHtml(String(sla))}</strong></td>
          <td>${notify}</td>
          <td>${waiver}</td>
        </tr>`;
      })
      .join("");
    const scheduleTable = `<table class="grid schedule">
        <thead>
          <tr>
            <th>Step</th>
            <th>Approver role</th>
            <th>SLA (calendar days)</th>
            <th>Notify-only</th>
            <th>Notice waiver (if applicable)</th>
          </tr>
        </thead>
        <tbody>${scheduleRows}</tbody>
      </table>
      <p class="table-caption"><strong>Schedule.</strong> SLA periods are measured in <strong>calendar days</strong> unless a particular case is varied in writing by HR. A “notify-only” step does not require an approval decision from the named role but serves to inform that role for governance and audit.</p>`;
    const items = steps
      .map((s) => {
        const stepNum = docNum.subsub();
        const detailNum = docNum.subsubsub();
        const r = roleLabel(s.role);
        const wv =
          s.noticeWaiverAllowed && Number(s.noticeWaiverMaxDays) > 0
            ? ` Where notice waiver is in scope for this step, waiver of up to <strong>${escapeHtml(String(s.noticeWaiverMaxDays))}</strong> calendar day(s) of notice may be considered in accordance with company rules.`
            : s.noticeWaiverAllowed
              ? " Notice waiver may be considered at this step where company rules allow."
              : "";
        const line = s.notifyOnly
          ? `This step is for <strong>notification only</strong> (no approval action required from ${escapeHtml(r)}).`
          : `${escapeHtml(r)} must complete the approval action within <strong>${s.slaDays ?? 3} calendar day(s)</strong> unless escalated or varied by HR.${wv}`;
        return `<li><p><strong>${stepNum} — ${escapeHtml(r)}.</strong></p><p class="step-detail"><strong>${detailNum}</strong> ${line}</p></li>`;
      })
      .join("");
    return `<h3>${escapeHtml(fullTitle)}</h3>${scheduleTable}<ol class="steps">${items}</ol>`;
  };

  const renderWorkflowOperationalParams = () => {
    if (p.approvalMethod !== "workflow") return "";
    const sec = docNum.sub();
    return `<h3>${sec} Operational parameters and service levels</h3>
      <p>The following table records the workflow controls configured for this policy version. They govern reminders, escalation, and how parallel or sequential approvals behave in the system. HR may adjust handling of individual cases where the law or business circumstances require.</p>
      <table class="grid key-value">
        <tbody>
          <tr><th scope="row">Approval mode</th><td>${escapeHtml(approvalMethodLabel(p.approvalMethod))}</td></tr>
          <tr><th scope="row">Reminder notifications</th><td>May be sent <strong>${escapeHtml(String(p.reminderDays ?? 7))}</strong> calendar day(s) before relevant deadlines, where the system supports this rule.</td></tr>
          <tr><th scope="row">Parallel approval</th><td><strong>${p.workflowParallelApproval ? "Enabled" : "Disabled"}</strong> — when enabled, designated approvers may act in parallel where the workflow allows.</td></tr>
          <tr><th scope="row">Escalation if no action</th><td>After <strong>${escapeHtml(String(p.workflowEscalationAfterDays ?? 3))}</strong> calendar day(s) without action, the case may be escalated per company rules.</td></tr>
          <tr><th scope="row">Auto-approval if no action</th><td><strong>${p.workflowAutoApprovalIfNoAction ? "Yes — where the system and policy allow" : "No"}</strong></td></tr>
          <tr><th scope="row">Rejection with reason</th><td><strong>${p.workflowRejectionWithReason !== false ? "Required / encouraged where configured" : "Optional"}</strong></td></tr>
          <tr><th scope="row">Re-initiation after rejection</th><td><strong>${p.workflowReinitiationAllowed !== false ? "Allowed subject to HR guidance" : "Restricted"}</strong></td></tr>
          ${p.needWorkflowSteps ? `<tr><th scope="row">Workflow steps required</th><td>Yes — cases should pass through defined steps where applicable.</td></tr>` : ""}
        </tbody>
      </table>`;
  };

  const renderNotificationsSection = () => {
    const alerts = p.alerts || {};
    const sec = docNum.major();
    const hr = alerts.hr !== false;
    const mgr = alerts.manager !== false;
    const emp = alerts.employee !== false;
    return `<h2>${sec}. Notifications and system alerts</h2>
    <p><strong>${docNum.sub()}.</strong> Subject to system configuration, notifications relating to separation under this policy may be directed as follows: HR <strong>${hr ? "on" : "off"}</strong>; people managers / reporting lines <strong>${mgr ? "on" : "off"}</strong>; the employee <strong>${emp ? "on" : "off"}</strong>. Disabling a channel in policy does not limit statutory notices or communications required by law.</p>`;
  };

  const renderAutoAssignSection = () => {
    const sec = docNum.major();
    const on = !!p.autoAssignOnJoin;
    return `<h2>${sec}. Assignment of this policy</h2>
    <p><strong>${docNum.sub()}.</strong> This policy version may be <strong>${on ? "automatically assigned to employees when they join" : "assigned manually or by HR rule — automatic assignment on joining is not enabled"}</strong> for employees who fall within its applicability criteria. Assignment in the HR system is evidence of which policy text applies; employees with questions should confirm with HR.</p>`;
  };

  const renderChecklistSection = () => {
    const sec = docNum.major();
    if (!checklist.length) {
      return `<h2>${sec}. Clearance checklist, SLAs, and dependencies</h2>
      <p><strong>${docNum.sub()}.</strong> No checklist tasks are listed in this version of the policy. HR may still assign handover, IT, finance, asset return, or other clearance tasks under general procedures or statutory requirements so that exit is completed safely and full &amp; final settlement can proceed.</p>`;
    }
    const rows = checklist
      .map((item, index) => {
        const ref = index + 1;
        const trig = separationTriggerLabel(item.triggerEvent);
        const deptRaw = item.department || "";
        const deptLabel =
          CHECKLIST_DEPT_LABELS[deptRaw] ||
          masterName(options.departmentsList, deptRaw) ||
          (deptRaw ? String(deptRaw) : null) ||
          "—";
        const actor = ACTION_ROLE_LABELS[item.actionRole] || item.actionRole || "—";
        const dueShort = formatDueRuleShort(item);
        const depOn = item.dependsOnTaskId
          ? escapeHtml(checklist.find((x) => x.taskId === item.dependsOnTaskId)?.name || item.dependsOnTaskId)
          : "— (none)";
        return `<tr>
          <td>${ref}</td>
          <td>${escapeHtml(trig)}</td>
          <td>${escapeHtml(deptLabel)}</td>
          <td>${escapeHtml(actor)}</td>
          <td>${escapeHtml(item.name || "—")}</td>
          <td>${item.mandatory ? "Mandatory" : "Optional"}</td>
          <td>${escapeHtml(dueShort)}</td>
          <td>${depOn}</td>
        </tr>`;
      })
      .join("");
    const detailLines = checklist
      .map((item, index) => {
        const ref = index + 1;
        return `<p><strong>${ref}.</strong> ${formatDueRuleLong(item)}</p>`;
      })
      .join("");
    return `
      <h2>${sec}. Clearance checklist, SLAs, and dependencies</h2>
      <p><strong>${docNum.sub()}.</strong> The following clearance and handover tasks are tied to <strong>trigger events</strong> (for example resignation approval). Each task has a <strong>due rule</strong> and may <strong>depend on</strong> completion of another task first. Mandatory tasks must be completed before exit can be finalised except where HR waives a step in writing. Optional tasks support good practice but may not block closure unless HR directs otherwise.</p>
      <table class="grid">
        <thead>
          <tr>
            <th>Ref.</th>
            <th>Trigger event</th>
            <th>Department / unit</th>
            <th>Responsible role</th>
            <th>Task</th>
            <th>Nature</th>
            <th>Due (summary)</th>
            <th>Depends on</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <h3>${docNum.sub()}. Due date rules for each clearance item</h3>
      ${detailLines}
      <p><strong>${docNum.sub()}.</strong> Days in the due rules above are <strong>calendar days</strong> unless the HR system applies a different convention for a module. Where a task depends on another, the dependent task should not be treated as complete until the predecessor is verified in the system.</p>`;
  };

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>${policyTitle} — ${companyName}</title>
  <style>
    body { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 12.5px; line-height: 1.6; color: #1e293b; max-width: 820px; margin: 0 auto; padding: 32px; }
    .letterhead { border-bottom: 2px solid #0f172a; padding-bottom: 18px; margin-bottom: 24px; }
    .company { font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 8px 0; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .doc-title { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 15px; font-weight: 700; color: #0f172a; margin: 16px 0 8px 0; text-transform: uppercase; letter-spacing: .06em; }
    .addr { margin: 0; color: #475569; font-size: 11px; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .control { margin-top: 14px; padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0; font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 11px; color: #334155; }
    .control strong { color: #0f172a; }
    p.meta { font-size: 11px; color: #64748b; margin-top: 10px; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    h2 { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 13px; margin: 22px 0 10px 0; color: #0f172a; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
    h3 { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 12px; margin: 14px 0 8px 0; color: #334155; }
    p { margin: 0 0 12px 0; text-align: justify; }
    ul { margin: 8px 0 12px 20px; }
    li { margin-bottom: 6px; }
    ol.steps { margin: 8px 0 16px 0; padding: 0; list-style-type: none; }
    ol.steps li { margin-bottom: 10px; }
    p.step-detail { margin: 4px 0 0 12px; font-size: 11.5px; color: #475569; }
    table.grid { width: 100%; border-collapse: collapse; font-size: 11px; margin: 10px 0 16px 0; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    table.grid th, table.grid td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; vertical-align: top; }
    table.grid th { background: #f1f5f9; font-weight: 600; color: #334155; }
    table.grid.key-value th { width: 32%; background: #f8fafc; }
    table.grid.schedule th { font-size: 10.5px; }
    p.table-caption { font-size: 10.5px; color: #64748b; font-family: "Segoe UI", Inter, Arial, sans-serif; margin-top: 6px; }
    .sign { margin-top: 32px; display: grid; grid-template-columns: 1fr 1fr; gap: 28px; font-size: 11px; color: #475569; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .sign .line { border-top: 1px solid #94a3b8; padding-top: 8px; margin-top: 36px; color: #0f172a; font-weight: 600; }
    @media print { body { padding: 18px; } }
  </style>
</head>
<body>
  <div class="letterhead">
    <p class="company">${companyName}</p>
    ${addressBlock}
    <p class="doc-title">HR separation policy</p>
    <p style='font-family:"Segoe UI",Inter,Arial,sans-serif;font-size:13px;margin:0 0 4px 0;'><strong>${policyTitle}</strong></p>
    <div class="control">
      <strong>Document version:</strong> ${escapeHtml(String(ver))} &nbsp;|&nbsp;
      <strong>Effective from:</strong> ${eff} &nbsp;|&nbsp;
      <strong>Owned by:</strong> ${escapeHtml(preparedBy)} / ${escapeHtml(approvedBy)}
    </div>
    ${pushedNote}
  </div>

  <h2>${docNum.major()}. Purpose</h2>
  <p><strong>${docNum.sub()}.</strong> This policy explains how <strong>${companyName}</strong> governs <strong>employee-initiated resignation</strong>, <strong>employer-led termination</strong>, and related events such as withdrawal or cancellation of a resignation, where those paths are enabled in the HR system. It covers how notice is counted, how approvals and workflows operate, which letters and templates may be used, what clearance tasks may apply, and how exit interviews and backdated resignations are treated — so that employees, managers, and HR share a common understanding of process and responsibility.</p>
  <p><strong>${docNum.sub()}.</strong> The policy is written in plain language for employees covered by it. It operates together with your employment contract, applicable statutes, and other HR policies of <strong>${companyName}</strong>. Where this document refers to workflows, reminders, or letters, the arrangements approved by HR and reflected in the system apply; this text describes the intended experience and obligations. Case-specific decisions remain with management and HR within the law.</p>

  <h2>${docNum.major()}. Applicability (who this policy covers)</h2>
  ${buildApplicabilityProse(p, { ...options, companyName: org })}
  <h3>${docNum.sub()}. What employees and managers are expected to do</h3>
  <p>If this policy applies to you, you are expected to: submit resignation or separation-related requests through official channels (for example the employee portal or process defined by HR); serve notice and attend work as required by your contract and this policy; complete clearance and handover tasks assigned to you; attend an exit interview where it is mandatory; and provide accurate dates and documents so that <strong>${companyName}</strong> can process full &amp; final settlement and service certificates fairly. Managers are expected to approve or route requests on time, document facts accurately, and escalate to HR where uncertainty arises.</p>

  <h2>${docNum.major()}. Definitions</h2>
  <p><strong>${docNum.sub()}. Resignation</strong> means a voluntary decision by an employee to end employment, initiated and recorded according to company process.</p>
  <p><strong>${docNum.sub()}. Termination</strong> means separation initiated by the employer, subject to due process, approvals, and any notice or payment rules that apply to the case.</p>
  <p><strong>${docNum.sub()}. Notice period</strong> means the length of time between communication of resignation or termination and the last working day (or such other measure as stated in your contract or statute), measured in <strong>calendar days</strong> or <strong>working days</strong> as configured below for probation and for confirmed employees.</p>
  <p><strong>${docNum.sub()}. Working days (by attendance)</strong> means notice where only certain attendance or leave status codes (for example present, approved leave codes) count toward each day of notice, as listed in this policy when that mode is selected.</p>
  <p><strong>${docNum.sub()}. Clearance</strong> means completion of tasks such as asset return, IT access closure, and department sign-off so that the employee may be released from duties and dues settled.</p>

  <h2>${docNum.major()}. Notice periods, resignation timing, and exit formalities</h2>
  <p><strong>${docNum.sub()}.</strong> Notice rules may differ between employees who are still in <strong>probation</strong> and those who are <strong>confirmed</strong>. The table below summarises the default notice basis and length configured for this policy version. Your appointment letter or contract may specify different notice; where the law or contract is more favourable to the employee, that prevails.</p>
  <table class="grid">
    <thead>
      <tr>
        <th>Group</th>
        <th>Notice measured in</th>
        <th>Default length</th>
        <th>Working-day notice (status codes)</th>
      </tr>
    </thead>
    <tbody>${noticeTableBody}</tbody>
  </table>
  <p><strong>${docNum.sub()}.</strong> ${
    p.noticeProbationType === "working" || p.noticeConfirmedType === "working"
      ? `Where <strong>working days</strong> apply, only days that match the organisation’s configured attendance or leave status codes (see table) count toward notice. Other days do not reduce the remaining notice unless HR approves an exception.`
      : `For this version, notice is measured in <strong>calendar days</strong> for both groups unless HR changes the configuration.`
  }</p>
  <h3>${docNum.sub()}. Exit interview</h3>
  <p>${
    p.exitInterviewMandatory === "yes"
      ? `An <strong>exit interview</strong> is <strong>mandatory</strong> under this policy. Feedback may be collected using template reference: <strong>${escapeHtml(p.exitInterviewFeedbackTemplate || "—")}</strong>. Employees should complete it honestly so that ${companyName} can improve workplace practices.`
      : `An exit interview is <strong>not</strong> mandatory under this policy version, though managers or HR may still invite feedback as good practice.`
  }</p>
  <h3>${docNum.sub()}. Backdated resignation</h3>
  <p>${
    yn(p.allowBackdatedResignation)
      ? `Backdated resignation dates may be allowed in the system where business rules permit. ${
          yn(p.backdatedProofMandatory)
            ? `Supporting <strong>proof of document upload is mandatory</strong> when the resignation date is in the past.`
            : `HR may request proof or clarification where dates are in the past.`
        }`
      : `Resignation dates should reflect the actual timeline of decision and communication. Backdating is not enabled under this policy version; contact HR if you believe an exception is required.`
  }</p>

  <h2>${docNum.major()}. How approvals and workflows operate</h2>
  <p>${approvalMethodProse} The mode recorded in the system for this policy is <strong>${escapeHtml(approvalMethodLabel(p.approvalMethod))}</strong>.</p>
  ${
    p.approvalMethod === "workflow"
      ? `${renderWorkflowOperationalParams()}
        ${wfStepsHtml(resWf, "Resignation approval workflow")}
        ${wfStepsHtml(termWf, "Termination approval workflow")}
        ${wfStepsHtml(cancelWf, "Cancellation or withdrawal workflow")}`
      : `<p><strong>${docNum.sub()}.</strong> No standing multi-step workflow is configured for every case in this version. HR will route resignation, termination, and cancellation matters using the approval mode described above, applicable law, and management direction. Individual cases may still use ad hoc approvals. Reminder, escalation, and parallel-approval settings apply when workflow mode is enabled; where this mode is not selected, those parameters may be inactive in the system.</p>`
  }

  <h2>${docNum.major()}. Letters, templates, and automation</h2>
  <p><strong>${docNum.sub()}.</strong> Communications may use the templates or identifiers selected for this policy. Relieving or resignation-related letters may follow: <strong>${escapeHtml(
    LETTER_RESIGN_LABELS[p.letterResignation] || p.letterResignation || "—"
  )}</strong>. Resignation acceptance may use: <strong>${escapeHtml(
    LETTER_RESIGN_LABELS[p.letterResignationAcceptance] || p.letterResignationAcceptance || "—"
  )}</strong>. Cancellation-related correspondence: <strong>${escapeHtml(p.letterCancellation || "—")}</strong>. Termination-related letters: <strong>${escapeHtml(
    LETTER_TERM_LABELS[p.letterTermination] || p.letterTermination || "—"
  )}</strong>. Where the policy assigns a cancellation letter to the employee record, use of that template: <strong>${
    p.useCancellationLetter ? "Yes" : "No"
  }</strong>${
    p.cancellationLetterFormat
      ? `; cancellation letter format reference: <strong>${escapeHtml(p.cancellationLetterFormat)}</strong>`
      : ""
  }${
    p.letterFormat
      ? `. General letter format reference: <strong>${escapeHtml(p.letterFormat)}</strong>`
      : ""
  }.</p>
  <p><strong>${docNum.sub()}.</strong> Automatic generation of letters (where enabled) reduces delay but does not replace managerial judgment or legal review where required. Auto letter on <strong>resignation</strong> path: <strong>${
    p.autoLetter === "yes" ? "Yes" : "No"
  }</strong>. Auto letter on <strong>termination</strong> path: <strong>${p.autoLetterTermination === "yes" ? "Yes" : "No"}</strong>. Employees should read any letter carefully; it may contain terms in addition to this policy.</p>
  <p>Official communications may be sent by email, employee portal, or physical letter as per <strong>${companyName}</strong> practice.</p>

  ${renderNotificationsSection()}
  ${renderAutoAssignSection()}

  ${renderChecklistSection()}

  <h2>${docNum.major()}. Disclaimer and interpretation</h2>
  <p><strong>${docNum.sub()}.</strong> This document is an employee-facing description of separation, resignation, and termination rules approved for <strong>${companyName}</strong> for the version and effective date shown on the first page. It explains how processes are intended to operate; it is not a contract by itself. Your employment contract, appointment letter, applicable statutes, and any collective or company-wide instruments may contain additional or different provisions — where there is a conflict, those documents and the law prevail.</p>
  <p><strong>${docNum.sub()}.</strong> Internal <strong>service levels (SLAs)</strong>, checklist due rules, and workflow timelines in this policy are process targets to support timely clearance and settlement. They are not a substitute for statutory notice, wages, or dispute-resolution timelines. If a statutory or contractual obligation is stricter or different, that obligation prevails.</p>
  <p><strong>${docNum.sub()}.</strong> <strong>${companyName}</strong> may update this policy from time to time. When a new version applies, HR will communicate material changes through appropriate channels. Approvals, tasks, and letters recorded in the HR system should align with the policy version assigned to the employee; if you are unsure how this policy applies to your situation, contact HR.</p>

  <div class="sign">
    <div>
      <div class="line">Prepared by (${escapeHtml(preparedBy)})</div>
      <span>Name &amp; signature</span>
    </div>
    <div>
      <div class="line">Approved by (${escapeHtml(approvedBy)})</div>
      <span>Name &amp; signature</span>
    </div>
  </div>
</body>
</html>`;
}

export function printSeparationPolicyDocumentHtml(htmlString) {
  const w = window.open("", "_blank");
  if (!w) {
    window.print();
    return;
  }
  w.document.write(htmlString);
  w.document.close();
  w.focus();
  setTimeout(() => {
    w.print();
    w.close();
  }, 300);
}
